import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
} from '@mui/material';
import { BackIcon } from 'components/SVG';
import PublishIcon from 'components/SVG/PublishIcon';
import { SparkButton, SparkEditableTitle } from 'sharedComponents/UI';
import {
  useAddSparkRating,
  useDeleteSpark,
  useSparkDiagnostics,
  useSparksByID,
  useUpdateSpark,
  useUpdateSparkRating,
} from 'hooks/spark';
import {
  useCurrentUser,
  useDialog,
  usePopover,
  useSafeNavigate,
} from 'hooks/common';
import SparkBadges from 'components/common/SparkBadges';
import { useSparkBuilderStore } from 'stores';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import PublishResourceModal from 'components/common/PublishResourceModal';
import SparkShareModal from 'components/common/Share/SparkShareModal';
import {
  useCreateFavouriteList,
  useCreateSparkFavourite,
  useDeleteSparkFavourite,
} from 'hooks/Favourites';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import { useSparkStatusType } from 'hooks/EntityTypes/useSparkStatusType';
import { useJoinedUser } from 'hooks/common/useJoinedUser';
import type { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import type { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import { SPARK_BADGE_TYPES } from 'constants/common';
import { SPARK_ROUTES } from 'constants/routes';
import { getCurrentDateISOString } from 'utilities/time';
import { SparkImageLoader } from 'sharedComponents/UI';
import { useIsFetching, useIsMutating } from 'react-query';
import PreviewIcon from 'components/SVG/PreviewIcon';
import SparkPreviewModal from './SparkPreview/SparkPreviewModal';

const CreateSparkHeader = React.forwardRef(
  function CreateSparkHeader(props, ref) {
    const { navigate, handleRouteBack } = useSafeNavigate();
    const sparkID = useSparkBuilderStore((store) => store.sparkID);
    const sparkSteps = useSparkBuilderStore((store) => store.sparkSteps.data);
    const setSelectedNavListItemID = useSparkBuilderStore(
      (store) => store.builderSideBarActions.setSelectedNavListItemID
    );
    const {
      mutateAsync: updateSpark,
      isLoading: isUpdatingSpark,
      isError: isUpdatingSparkError,
    } = useUpdateSpark(sparkID);

    const { mutateAsync: deleteSpark, isLoading: isDeletingSpark } =
      useDeleteSpark(sparkID);

    const { idByNameMap: sparkStatusTypes } = useSparkStatusType();

    const { currentUserID } = useCurrentUser();

    const { sparkDiagnostics } = useSparkDiagnostics(sparkID);
    const { isLoading, joinedSpark: spark } = useSparksByID(sparkID);
    const { mutateAsync: createFavoriteList } = useCreateFavouriteList();

    const {
      mutateAsync: createSparkFavorites,
      isLoading: isCreatingSparkFavourite,
    } = useCreateSparkFavourite();

    const {
      mutateAsync: deleteSparkFavorites,
      isLoading: isDeletingSparkFavourite,
    } = useDeleteSparkFavourite();

    const { mutateAsync: addUserFlag } = useAddUserFlag();
    const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();
    const { marketplaceAccounts } = useJoinedUser();

    const {
      open: isSparkPublishDialogOpen,
      openDialog: openSparkPublishDialog,
      closeDialog: closeSparkPublishDialog,
    } = useDialog();

    const {
      open: isSparkPreviewDialogOpen,
      openDialog: openSparkPreviewDialog,
      closeDialog: closeSparkPreviewDialog,
    } = useDialog();

    const [isArchivingSpark, setIsArchivingSpark] = useState(false);

    const {
      anchorEl: sparkShareAnchorEl,
      open: isSparkSharePopoverOpen,
      openPopover: openSparkSharePopover,
      closePopover: closeSparkSharePopover,
    } = usePopover();

    const state = isUpdatingSpark
      ? 'loading'
      : isUpdatingSparkError
        ? 'error'
        : 'success';

    const existingFavouriteList = spark?.meta.user.favouriteLists;

    const publishDisabled =
      sparkDiagnostics?.diagnostics?.hasIssues === undefined ||
      sparkDiagnostics?.diagnostics?.hasIssues === null ||
      sparkDiagnostics?.publish?.canPublish === false ||
      sparkDiagnostics?.diagnostics?.hasIssues;
    const hasMarketPlaceAccount = !!marketplaceAccounts.length;
    const sparkName = spark?.attributes.nameforHuman;
    const handleCreateList = async (values: CreateFavouriteListFormValues) => {
      const res = await createFavoriteList(values.listName);

      const sparkFavourite = {
        sparkID: spark?.id,
        userID: currentUserID,
        favouriteListID: Number(res.data.id),
      };
      await createSparkFavorites(sparkFavourite);
    };

    const handleFavouriteListItem = async (listID: number) => {
      if (existingFavouriteList?.some((listItem) => listItem.id === listID)) {
        const sparkFavourite = spark?.sparkFavourites?.find(
          (favourite) => favourite.attributes.favouriteListID === listID
        );

        await deleteSparkFavorites(sparkFavourite?.id || '');
        return;
      }
      const sparkFavourite = {
        sparkID: spark?.id,
        userID: currentUserID,
        favouriteListID: listID,
      };
      await createSparkFavorites(sparkFavourite);
    };
    const { ratings, permissions } = spark?.meta.user || {};

    const allowedToReview = permissions?.canReview;

    const userRating = ratings?.find((rating) => rating.sparkID === spark?.id);

    const { mutateAsync: addSparkRating } = useAddSparkRating();
    const { mutateAsync: updateSparkRating } = useUpdateSparkRating(
      userRating?.id ? `${userRating?.id}` : undefined
    );
    const handleSparkRating = async (values: ReviewFormModalValues) => {
      const doesReviewExists = !!userRating;
      if (!doesReviewExists) {
        await addSparkRating({ ...values, sparkID: spark?.id });
      }
      if (doesReviewExists) {
        await updateSparkRating({ ...values, sparkID: spark?.id });
      }
    };

    const handleModalClose = () => {
      setSelectedNavListItemID(null);
      closeSparkPublishDialog();
    };
    const handlePreviewModalClose = () => {
      closeSparkPreviewDialog();
    };

    const onRun = () => {
      handleModalClose();
      window.open(`${SPARK_ROUTES.SPARK_RUNS}/${spark?.id}`);
      setSelectedNavListItemID(null);
    };

    const handleSendReport = async (values: SparkReportFormValues) => {
      const userFlag = {
        flagReasonID: +values.reasonID,
        sparkID: spark?.id,
        comments: values.comments,
      };
      await addUserFlag(userFlag);
    };
    const isMutating = !!useIsMutating();
    const isFetching = !!useIsFetching();

    const handleDeleteSpark = async () => {
      await deleteSpark({
        deletedAt: getCurrentDateISOString(),
        isDeleted: true,
      });
      navigate('/dashboard');
    };

    const handleArchiveSpark = async () => {
      setIsArchivingSpark(true);
      try {
        await updateSpark({
          isArchived: true,
        });
      } catch (error) {
        console.error('Error archiving Spark:', error);
        toastr.error('Failed to archive the Spark. Please try again.');
      } finally {
        setTimeout(() => {
          setIsArchivingSpark(false);
          navigate('/dashboard');
        }, 2500);
      }
    };
    const userPermissions = spark?.meta.user.permissions;

    const isLoadingSparkFavourites =
      isCreatingSparkFavourite || isDeletingSparkFavourite || isLoading;
    return (
      <AppBar ref={ref as any}>
        <Grid
          container
          item
          justifyContent='space-between'
          position='fixed'
          bgcolor={(theme) => theme.palette.common.white}
          height='4rem'
          sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.borderColor}`,
          }}
          wrap='nowrap'
        >
          <Grid
            container
            gap={1}
            alignItems='center'
            marginLeft={1.5}
            wrap='nowrap'
          >
            <Box>
              <Button
                onClick={handleRouteBack}
                sx={{
                  minWidth: 'unset',
                  width: '45px',
                  height: '45px',
                  padding: 0,
                  borderWidth: '1px',
                  borderRadius: '14px',
                  borderColor: '#EEEEEE',
                  aspectRatio: 1,
                }}
                variant='outlined'
              >
                <BackIcon />
              </Button>
            </Box>
            <SparkEditableTitle
              value={sparkName}
              onTitleSave={(newTitle) =>
                updateSpark({ nameforHuman: newTitle })
              }
              isParentLoading={!sparkName}
              inputStyle={{ maxWidth: 'unset', paddingRight: 0 }}
              characterLengthOffSet={-2}
            />
            <SparkBadges
              badges={
                spark?.meta.badges.filter(
                  (item) => item.type === SPARK_BADGE_TYPES.Status
                ) || []
              }
              chipLabelStyles={{ paddingRight: 2, paddingLeft: 2 }}
            />
          </Grid>
          <Grid container justifyContent='center' alignItems='center'>
            <SparkImageLoader
              src='/SparkLogo.svg'
              alt='spark-logo'
              height={26.79}
              width={201}
            />
          </Grid>
          <Grid container justifyContent='flex-end' alignItems='center'>
            <Box display='flex' gap={2} marginRight={2} alignItems='center'>
              {(isMutating || isFetching) && (
                <CircularProgress
                  variant='indeterminate'
                  thickness={8}
                  sx={{
                    width: `30px !important`,
                    height: `30px !important`,
                  }}
                />
              )}
              <SparkButton
                variant='outlined'
                startIcon={<PreviewIcon />}
                sx={{ height: '2.5rem' }}
                onClick={() => {
                  openSparkPreviewDialog();
                }}
              >
                Preview
              </SparkButton>

              {sparkSteps?.length !== 0 && (
                <Tooltip
                  title={
                    publishDisabled
                      ? 'Fix your Spark to publish'
                      : !hasMarketPlaceAccount
                        ? 'Activate Marketplace Account'
                        : ''
                  }
                >
                  <span
                  // style={{
                  //   position: 'relative',
                  //   display: 'flex',
                  //   gap: '0.5rem',
                  // }}
                  >
                    <SparkButton
                      variant='contained'
                      startIcon={<PublishIcon />}
                      sx={{ height: '2.5rem' }}
                      disabled={publishDisabled || !hasMarketPlaceAccount}
                      onClick={() => {
                        updateSpark({
                          sparkStatusTypeID: Number(
                            sparkStatusTypes?.Published
                          ),
                        });
                        openSparkPublishDialog();
                      }}
                    >
                      Publish
                    </SparkButton>
                  </span>
                </Tooltip>
              )}
              <SparkOptionsModal
                resourceLogoURL={spark?.attributes.iconURL || ''}
                options={spark?.meta.options}
                resourceID={spark?.id}
                handleSparkRatingSave={handleSparkRating}
                userRating={userRating}
                allowedToReview={allowedToReview}
                resourceType='spark'
                privacyTypeId={spark?.attributes.sparkPrivacyTypeID}
                resourceAccessLinkToken={spark?.attributes.accessLinkToken}
                onFavouriteItemClick={handleFavouriteListItem}
                existingFavouriteList={existingFavouriteList || []}
                onCreateList={handleCreateList}
                isLoading={
                  isLoadingSparkFavourites ||
                  isDeletingSpark ||
                  isArchivingSpark
                }
                onSendReport={handleSendReport}
                flagReasonsDropdownOptions={flagReasonsDropdownOptions}
                isResourceFavourite={spark?.meta.user.isFavourited}
                onDelete={handleDeleteSpark}
                onArchive={handleArchiveSpark}
                showHeartIcon={false}
                showShareIcon={false}
                userPermissions={userPermissions}
                resourceName={spark?.attributes.nameforHuman || ''}
              />
              {isSparkPreviewDialogOpen && (
                <SparkPreviewModal
                  open={isSparkPreviewDialogOpen}
                  onClose={handlePreviewModalClose}
                />
              )}
              {isSparkPublishDialogOpen && (
                <PublishResourceModal
                  title={spark?.attributes.nameforHuman || ''}
                  resource='Spark'
                  open={isSparkPublishDialogOpen}
                  closeDialog={handleModalClose}
                  state={state}
                  onShare={openSparkSharePopover}
                  onRun={onRun}
                />
              )}
              {isSparkSharePopoverOpen && (
                <SparkShareModal
                  resourceType='spark'
                  handleClose={closeSparkSharePopover}
                  open={isSparkPublishDialogOpen}
                  resourceID={spark?.id}
                  anchorEl={sparkShareAnchorEl}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </AppBar>
    );
  }
);

export default CreateSparkHeader;
